import React, { useContext, useEffect } from "react";
import SettingsDisplay from "../components/SettingsDisplay";
import ManageReviewsDisplay from "../components/ManageReviewsDisplay";
import { DisplaySettings, Settings } from "../models/shared/Settings";
import { useFirestore } from "reactfire";
import { useComponentId } from "../hooks/useComponentId";
import { doc, onSnapshot } from 'firebase/firestore';
import { Collections } from "../utils/shared/constants";
import { updateSettings } from "../utils/api";

interface SettingsContext {
    currentSection: NavigationSection;
    navigate: (section: NavigationSection) => void;

    settings: null | DisplaySettings;
    updateSettings: (settings: DisplaySettings) => void;

    processing: boolean;
    setProcessing: (processing: boolean) => void;
}

type NavigationSection = 'home' | 'manageReviews';

const SettingsNavigationContext = React.createContext<SettingsContext>({
    currentSection: 'home',
    navigate: () => { },
    settings: null,
    updateSettings: () => { },
    processing: false,
    setProcessing: () => { },
});

export const useSettingsContext = () => useContext(SettingsNavigationContext);

export const SettingsNavigationProvider = () => {
    const [currentSection, setCurrentSection] = React.useState<NavigationSection>('home');
    const [settings, setSettings] = React.useState<null | DisplaySettings>(null);
    const [processing, setProcessing] = React.useState(false);
    const db = useFirestore();
    const componentId = useComponentId();

    useEffect(() => {
        if (componentId && db) {
            return onSnapshot(doc(db, Collections.settings, componentId), snapshot => {
                if (snapshot.exists()) {
                    const data = snapshot.data() as Settings;
                    setSettings(data.editor);
                } else {
                    setSettings({})
                }
            })
        }
    }, [componentId, db])

    const handleSettingsChange = async (newData: Partial<DisplaySettings>) => {
        setProcessing(true);
        await updateSettings({ ...settings, ...newData })
        setProcessing(false);
    }


    return (
        <SettingsNavigationContext.Provider value={{
            currentSection, navigate: setCurrentSection,
            settings, updateSettings: handleSettingsChange,
            processing, setProcessing
        }}>
            <SettingsContent />
        </SettingsNavigationContext.Provider>
    );
}

const SettingsContent = () => {
    const { currentSection } = useSettingsContext();

    switch (currentSection) {
        case 'home':
            return <SettingsDisplay />
        case 'manageReviews':
            return <ManageReviewsDisplay />
        default:
            return null;
    }
}